import axiosInstance from "api/axiosInstance";
import { toast } from "react-toastify";
import React, { useState } from "react";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import {
    CheckCircle2,
    XCircle,
    AlertTriangle,
    Info,
    Sparkles,
    Trash2,
    LogOut,
    Layers,
    Loader2,
    ShieldCheck,
} from "lucide-react";

/* =========================================================
   TOAST THEME HELPERS
========================================================= */

const TOAST_BASE_CLASSES = `
    !rounded-2xl
    !bg-white/90 dark:!bg-slate-900/90
    !backdrop-blur-2xl
    !ring-1 !ring-slate-200/70 dark:!ring-white/10
    !shadow-[0_20px_60px_-25px_rgba(13,148,136,0.35)]
    dark:!shadow-[0_20px_60px_-25px_rgba(0,0,0,0.8)]
    !text-slate-800 dark:!text-slate-100
    !font-sans
    overflow-hidden
    text-center
`;

/* =========================================================
   Simple Toast
========================================================= */

export const showToast = (message, type = "info") => {
    const iconMap = {
        success: <CheckCircle2 className="w-4 h-4" />,
        error: <XCircle className="w-4 h-4" />,
        warning: <AlertTriangle className="w-4 h-4" />,
        info: <Info className="w-4 h-4" />,
    };

    const accentMap = {
        success: "from-emerald-500 to-teal-600",
        error: "from-rose-500 to-red-600",
        warning: "from-amber-400 to-orange-500",
        info: "from-teal-500 to-teal-700",
    };

    const content = (
        <div className="flex items-center gap-3 py-1">
            <span
                className={`relative flex-shrink-0 w-8 h-8 rounded-xl
                            bg-gradient-to-br ${accentMap[type] || accentMap.info}
                            flex items-center justify-center text-white
                            shadow-lg`}
            >
                {iconMap[type] || iconMap.info}
                <span className="absolute inset-0 rounded-xl ring-1 ring-white/25" />
            </span>
            <p className="text-[13px] font-semibold leading-5">{message}</p>
        </div>
    );

    const options = {
        className: TOAST_BASE_CLASSES,
        icon: false,
        closeButton: false,
        hideProgressBar: true,
    };

    switch (type) {
        case "success":
            toast.success(content, options);
            break;
        case "error":
            toast.error(content, options);
            break;
        case "warning":
            toast.warning(content, options);
            break;
        default:
            toast.info(content, options);
    }
};

/* =========================================================
   Confirm Toast (Reusable Base)
========================================================= */

const ConfirmToastShell = ({
    closeToast,
    icon: Icon,
    iconGradient,
    title,
    messages = [],
    confirmLabel,
    cancelLabel,
    onConfirm,
    confirmClass = "from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 shadow-rose-500/30",
    loading = false,
}) => (
    <div className="w-full max-w-sm py-1 ">
        {/* header */}
        <div className="flex items-start gap-3 mb-4">
            <span
                className={`flex-shrink-0 relative w-10 h-10 rounded-2xl
                            bg-gradient-to-br ${iconGradient}
                            flex items-center justify-center text-white
                            shadow-lg`}
            >
                <Icon className="w-5 h-5" />
                <span className="absolute inset-0 rounded-2xl ring-1 ring-white/25" />
            </span>

            <div className="flex-1 pt-1">
                {messages.map((m, i) => (
                    <p
                        key={i}
                        className={`text-[13px] leading-6 font-medium
                                    text-slate-700 dark:text-slate-300
                                    ${i === 0 ? "font-bold text-slate-800 dark:text-slate-100" : "mt-1"}`}
                    >
                        {m}
                    </p>
                ))}
            </div>
        </div>

        {/* actions */}
        <div className="flex gap-2 justify-end">
            <button
                type="button"
                onClick={closeToast}
                disabled={loading}
                className="
                    px-4 py-2 rounded-xl
                    text-[12px] font-bold
                    text-slate-600 dark:text-slate-300
                    bg-slate-100/80 dark:bg-slate-800/70
                    ring-1 ring-slate-200/70 dark:ring-white/10
                    hover:bg-slate-200/90 dark:hover:bg-slate-700/70
                    active:scale-95
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-all duration-200
                "
            >
                {cancelLabel}
            </button>

            <button
                type="button"
                onClick={() => {
                    onConfirm?.();
                    if (!loading) closeToast();
                }}
                disabled={loading}
                className={`
                    px-4 py-2 rounded-xl
                    text-[12px] font-bold text-white
                    bg-gradient-to-r ${confirmClass}
                    shadow-lg
                    active:scale-95
                    disabled:opacity-60 disabled:cursor-not-allowed
                    transition-all duration-200
                    flex items-center gap-1.5
                    min-w-[90px] justify-center
                `}
            >
                {loading ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                    <CheckCircle2 className="w-3.5 h-3.5" />
                )}
                {confirmLabel}
            </button>
        </div>
    </div>
);

const CONFIRM_OPTIONS = {
    autoClose: false,
    closeOnClick: false,
    closeButton: false,
    draggable: false,
    position: "top-center",
    className: TOAST_BASE_CLASSES,
};

/* =========================================================
   Confirm Delete Toast
========================================================= */

export const confirmToast = (onConfirm) => {
    toast(
        ({ closeToast }) => (
            <ConfirmToastShell
                closeToast={closeToast}
                icon={Trash2}
                iconGradient="from-rose-500 to-red-600"
                title="confirm-delete"
                messages={[i18n.t("user:toasts.deleteConfirm.message")]}
                confirmLabel={i18n.t("user:toasts.deleteConfirm.confirm")}
                cancelLabel={i18n.t("user:toasts.deleteConfirm.cancel")}
                onConfirm={onConfirm}
                confirmClass="from-rose-500 to-red-600 hover:from-rose-600 hover:to-red-700 shadow-rose-500/30"
            />
        ),
        CONFIRM_OPTIONS
    );
};

/* =========================================================
   Confirm End Conversation Toast
========================================================= */

export const confirmEndToast = (onConfirm) => {
    toast(
        ({ closeToast }) => (
            <ConfirmToastShell
                closeToast={closeToast}
                icon={LogOut}
                iconGradient="from-amber-400 to-orange-500"
                title="confirm-end"
                messages={[i18n.t("user:toasts.endConversation.message")]}
                confirmLabel={i18n.t("user:toasts.endConversation.confirm")}
                cancelLabel={i18n.t("user:toasts.endConversation.cancel")}
                onConfirm={onConfirm}
                confirmClass="from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 shadow-amber-500/30"
            />
        ),
        CONFIRM_OPTIONS
    );
};

/* =========================================================
   Confirm Plan Models Toast
========================================================= */

export const confirmPlanModelsToast = (onConfirm) => {
    toast(
        ({ closeToast }) => (
            <ConfirmToastShell
                closeToast={closeToast}
                icon={Layers}
                iconGradient="from-teal-600 to-teal-800"
                title="confirm-plan"
                messages={[
                    i18n.t("user:toasts.planModelsConfirm.message1"),
                    i18n.t("user:toasts.planModelsConfirm.message2"),
                ]}
                confirmLabel={i18n.t("user:toasts.planModelsConfirm.confirm")}
                cancelLabel={i18n.t("user:toasts.planModelsConfirm.cancel")}
                onConfirm={onConfirm}
                confirmClass="from-teal-600 to-teal-800 hover:from-teal-500 hover:to-teal-700 shadow-teal-500/30"
            />
        ),
        CONFIRM_OPTIONS
    );
};

/* =========================================================
   Free Plan Toast Component
========================================================= */

function FreePlanToast({ message, closeToast, onConfirm }) {
    const { t } = useTranslation(["user"]);
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        if (loading) return;
        setLoading(true);

        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 10000);

        try {
            const res = await axiosInstance.patch(
                "/business/@me/roadmap/dismiss-free-plan",
                {},
                { signal: controller.signal }
            );
            clearTimeout(timeout);

            if (res?.data?.ok) {
                closeToast();
                if (typeof onConfirm === "function") onConfirm();
            } else {
                throw new Error("Response not ok");
            }
        } catch (err) {
            clearTimeout(timeout);
            console.error("Error confirming free plan:", err);
            setLoading(false);

            if (err?.name === "CanceledError" || err?.name === "AbortError") {
                toast.error(t("toasts.freePlan.timeoutError"));
            } else {
                toast.error(t("toasts.freePlan.submitError"));
            }
        }
    };

    return (
        <div className="w-full max-w-sm py-1">
            {/* header */}
            <div className="flex items-start gap-3 mb-4">
                <span
                    className="flex-shrink-0 relative w-10 h-10 rounded-2xl
                               bg-gradient-to-br from-amber-400 via-teal-500 to-teal-700
                               flex items-center justify-center text-white
                               shadow-lg shadow-teal-500/30"
                >
                    <Sparkles className="w-5 h-5" />
                    <span className="absolute inset-0 rounded-2xl ring-1 ring-white/25" />
                </span>

                <div className="flex-1 pt-1">
                    <p className="text-[13px] leading-6 font-bold text-slate-800 dark:text-slate-100">
                        {message || t("toasts.freePlan.defaultMessage")}
                    </p>
                </div>
            </div>

            {/* actions */}
            <div className="flex gap-2 justify-end">
                <button
                    type="button"
                    onClick={closeToast}
                    disabled={loading}
                    className="
                        px-4 py-2 rounded-xl
                        text-[12px] font-bold
                        text-slate-600 dark:text-slate-300
                        bg-slate-100/80 dark:bg-slate-800/70
                        ring-1 ring-slate-200/70 dark:ring-white/10
                        hover:bg-slate-200/90 dark:hover:bg-slate-700/70
                        active:scale-95
                        disabled:opacity-50 disabled:cursor-not-allowed
                        transition-all duration-200
                    "
                >
                    {t("toasts.freePlan.cancel") || "انصراف"}
                </button>

                <button
                    type="button"
                    disabled={loading}
                    onClick={handleConfirm}
                    className="
                        relative px-4 py-2 rounded-xl
                        text-[12px] font-bold text-white
                        bg-gradient-to-r from-teal-600 via-teal-500 to-amber-500
                        bg-[length:200%_100%] hover:bg-[position:100%_0]
                        shadow-lg shadow-teal-500/30
                        active:scale-95
                        disabled:opacity-60 disabled:cursor-not-allowed
                        transition-all duration-500
                        flex items-center justify-center gap-1.5
                        min-w-[110px]
                    "
                >
                    {loading ? (
                        <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            {t("toasts.freePlan.submitting")}
                        </>
                    ) : (
                        <>
                            <ShieldCheck className="w-3.5 h-3.5" />
                            {t("toasts.freePlan.confirm")}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

/* =========================================================
   Confirm Free Plan
========================================================= */

export const confirmFreePlan = (message, onConfirm) => {
    const toastId = "free-plan-confirm";

    if (toast.isActive(toastId)) return;

    toast(
        ({ closeToast }) => (
            <FreePlanToast
                message={message}
                closeToast={closeToast}
                onConfirm={onConfirm}
            />
        ),
        {
            ...CONFIRM_OPTIONS,
            toastId,
        }
    );
};